import React, { useCallback, useEffect, useState, type FC } from 'react';
import type { GraphQLTaggedNode } from 'react-relay';
import { graphql } from 'react-relay';
import { defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { ConfluencePageAri } from '@atlassian/ari';

import { expVal } from '@confluence/feature-experiments';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useQueryInterop, useMutationInterop } from '@confluence/apollo-relay-interop';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { DialogsContainerProps } from '@confluence/dialogs/entry-points/DialogsStateContainer.ts';
import { NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE } from '@confluence/access-requests/entry-points/NewAccessRequestIndicatorState.ts';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import {
	ResolveRestrictionsDialog,
	type ResolveRestrictionsDialogProps,
} from '../resolveRestrictions/ResolveRestrictionsDialog.tsx';
import { validateRestrictedPrincipals } from '../resolveRestrictions/validateRestrictedPrincipals.ts';
import type { RestrictedUserPrincipal } from '../commonTypes.tsx';
import { preloadSpaceRoleMode } from '../../controller/preloadSpaceRoleMode.ts';

import type {
	ResourceAccessType,
	useRealtimeAccessRequestsFlagsQuery$data,
	useRealtimeAccessRequestsFlagsQuery$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsQuery.graphql.ts';
import type {
	useRealtimeAccessRequestsFlagsMutation$data,
	useRealtimeAccessRequestsFlagsMutation$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsMutation.graphql.ts';
import type {
	useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$data,
	useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$variables,
} from './__generated__/useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation.graphql.ts';
import {
	DEFAULT_MINUTE_LOOKBACK,
	ONE_YEAR_IN_MINUTES,
	clearLastSeenRealtimeRequestCacheIfExpired,
	isMoreRecentThanCachedRequestTimestamp,
	updateLastSeenRealtimeRequestCache,
} from './lastSeenRequestCachingUtils.ts';
import {
	FlagRemovalDetector,
	makeFlagActions,
	makeFlagCustomIcon,
	makeFlagDescription,
	makeFlagTitle,
	makeSuccessFlagDescription,
	extractRestrictionInfo,
	makeFlagIdSuffix,
} from './makeFlagUtils.tsx';
import type { AccessRequestsList } from './realtimeAccessRequestTypes.ts';
import { useDocumentActive } from './useDocumentActive.ts';

const REQUEST_ALREADY_PROCESSED = 'REQUEST_ALREADY_PROCESSED';
const REALTIME_ACCESS_REQUEST_FLAG = 'realtime-access-request-flag';
const REALTIME_ACCESS_REQUEST_FLAG_ID = `${REALTIME_ACCESS_REQUEST_FLAG}-id`;
const SPACE_COLLABORATOR_ROLE_ID = '0c103ff5-44d0-4b2a-b80d-8690442c95f0';
const SPACE_VIEWER_ROLE_ID = '26d75a6e-ad72-42c3-bf30-007d3ebae9ed';
const NOOP = () => {};

const i18n = defineMessages({
	requestApprovedSuccess: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.request-approved-success',
		defaultMessage: 'Request approved',
		description: 'Title for success flag when request is approved',
	},
	requestDeniedSuccess: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.request-denied-success',
		defaultMessage: 'Request denied',
		description: 'Title for success flag when request is denied',
	},
	denialSuccessDescription: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.denial-success-description',
		defaultMessage: "{displayName}'s access to this content won't change.",
		description: 'Description for success flag when request is denied',
	},
	requestAlreadyProcessedTitle: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.request-already-processed-title',
		defaultMessage: 'Someone already responded to this request',
		description: 'Title for info flag when request was already processed',
	},
	errorResolvingRequestTitle: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.error-resolving-request-title',
		defaultMessage: 'We ran into a problem resolving the access request. Try again later.',
		description: 'Title for error flag when resolving request fails',
	},
});

interface RealtimeAccessRequestsFlagsProps {
	userId: string;
	cloudId: string;
	edition: ConfluenceEdition.STANDARD | ConfluenceEdition.PREMIUM;
	contentType: string;
	contentId: string;
	pageTitle: string | null | undefined;
	onReviewRequests: () => void;
	flags: FlagsStateContainer;
	isContentRestricted?: boolean;
}

type ResolveRestrictionsDialogWrapperProps = DialogsContainerProps & ResolveRestrictionsDialogProps;
const ResolveRestrictionsDialogWrapper: FC<ResolveRestrictionsDialogWrapperProps> = (props) => (
	<ResolveRestrictionsDialog {...props} />
);

interface FireAnalyticEventArgs {
	eventType: 'sendTrackEvent' | 'sendScreenEvent' | 'sendUIEvent' | 'sendOperationalEvent';
	action: string;
	actionSubject: string;
	actionSubjectId?: string;
	attributes?: any;
	source?: string;
}

const UseRealtimeAccessRequestsQuery = graphql`
	query useRealtimeAccessRequestsFlagsQuery(
		$cloudId: ID!
		$contentId: ID!
		$duration: Int = 5
		$limit: Int = 10
		$start: String
	) {
		confluence_getLatestPendingRequests(
			cloudId: $cloudId
			contentId: $contentId
			duration: $duration
			limit: $limit
			start: $start
		) {
			latestPendingRequest {
				id
				requestAccessType
				creatorAaid
				creationDateTimestamp
				user {
					id
					displayName
					photos {
						isPrimary
						value
					}
				}
			}
		}
	}
`;

export const UseRealtimeAccessRequestsMutation: GraphQLTaggedNode = graphql`
	mutation useRealtimeAccessRequestsFlagsMutation(
		$cloudId: ID!
		$updateContentAccessRequestInput: ConfluenceUpdateContentAccessRequestInput!
	) {
		confluence_updateContentAccessRequest(
			cloudId: $cloudId
			updateContentAccessRequestInput: $updateContentAccessRequestInput
		) {
			success
			errors {
				message
			}
		}
	}
`;

const ResolveRestrictionsMutation = graphql`
	mutation useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation(
		$accessType: ResourceAccessType!
		$contentId: Long!
		$subjects: [BlockedAccessSubjectInput]!
		$spaceRoleId: ID
	) {
		resolveRestrictionsForSubjects(
			input: {
				accessType: $accessType
				contentId: $contentId
				subjects: $subjects
				spaceRoleId: $spaceRoleId
			}
		) {
			success
			errors {
				message
			}
		}
	}
`;

export const removeOwnRequests = (
	requests: AccessRequestsList,
	userId: string | null,
): ({
	readonly creationDateTimestamp: AGG$Long | null;
	readonly creatorAaid: string | null;
	readonly id: string;
	readonly requestAccessType: ResourceAccessType;
	readonly user: {
		readonly displayName: string | null;
		readonly id: string | null;
		readonly photos: ReadonlyArray<{
			readonly isPrimary: boolean | null;
			readonly value: string | null;
		} | null> | null;
	} | null;
} | null)[] =>
	requests.filter((request: AccessRequestsList[number]) => request?.creatorAaid !== userId);

// Called in response to a ping that there is a real-time access request
export const useRealtimeAccessRequestsFlags = ({
	userId,
	cloudId,
	edition,
	contentId,
	contentType,
	pageTitle,
	flags,
	onReviewRequests,
	isContentRestricted = true,
}: RealtimeAccessRequestsFlagsProps): void => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isRbacEnabled, loading: rbacStatusLoading } = useSpaceRoleMode();
	const { showModal } = useDialogs();
	const [hasError, setHasError] = useState(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const isTabActive = useDocumentActive();

	const skipConditions = hasError || rbacStatusLoading || !isTabActive;
	const markAsErrored = useCallback((error: ApolloErrorInterop | Error | undefined) => {
		setHasError(true);
		markErrorAsHandled(error);
	}, []);

	const fireAnalyticEvent = useCallback(
		(args: FireAnalyticEventArgs) => {
			createAnalyticsEvent({
				type: args.eventType,
				data: {
					source: args.source,
					action: args.action,
					actionSubject: args.actionSubject,
					actionSubjectId: args.actionSubjectId,
					attributes: args.attributes,
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const [mutateRequest] = useMutationInterop<
		useRealtimeAccessRequestsFlagsMutation$data,
		useRealtimeAccessRequestsFlagsMutation$variables
	>(UseRealtimeAccessRequestsMutation);

	const [resolveRestrictionsForSubjects] = useMutationInterop<
		useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$data,
		useRealtimeAccessRequestsFlagsResolveRestrictionsForSubjectsMutation$variables
	>(ResolveRestrictionsMutation);

	const onProcessRequestClick = useCallback(
		async (
			request: AccessRequestsList[number],
			decision: 'APPROVE' | 'DENY',
			restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined,
		) => {
			// Purely to satisfy the linter
			if (
				!request ||
				!request.id ||
				!request.requestAccessType ||
				!request.user ||
				!request.user.id ||
				!request.user.displayName ||
				!request.creatorAaid ||
				!request.creationDateTimestamp
			) {
				return;
			}

			const result = await mutateRequest({
				variables: {
					cloudId,
					updateContentAccessRequestInput: {
						contentId,
						requestId: request.id,
						accessType: request.requestAccessType,
						accessRequestedAccountId: request.user.id,
						approvalDecision: decision,
					},
				},
			});

			let successfulMutation = result.data?.confluence_updateContentAccessRequest?.success;
			const requestAlreadyProcessed =
				result?.data?.confluence_updateContentAccessRequest?.errors?.find(
					(err) => err?.message === REQUEST_ALREADY_PROCESSED,
				);

			// If user is blocked, run mutation to resolve restrictions
			if (successfulMutation && decision === 'APPROVE' && restrictedUserPrincipalInfo) {
				const rbacSpaceRole =
					request.requestAccessType === 'EDIT' ? SPACE_COLLABORATOR_ROLE_ID : SPACE_VIEWER_ROLE_ID;
				const spaceRoleId = isRbacEnabled ? rbacSpaceRole : null;
				const subjectId = request.creatorAaid;
				const subjectType = 'USER';

				const result = await resolveRestrictionsForSubjects({
					variables: {
						contentId: Number(contentId),
						accessType: request.requestAccessType,
						subjects: [{ subjectId, subjectType }],
						spaceRoleId,
					},
				});

				successfulMutation = result?.data?.resolveRestrictionsForSubjects?.success;
			}

			if (successfulMutation) {
				void flags.showSuccessFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_SUCCESS`,
					title: formatMessage(
						decision === 'APPROVE' ? i18n.requestApprovedSuccess : i18n.requestDeniedSuccess,
					),
					description:
						decision === 'APPROVE'
							? makeSuccessFlagDescription(
									restrictedUserPrincipalInfo,
									request.user.displayName,
									request.requestAccessType,
									formatMessage,
								)
							: formatMessage(i18n.denialSuccessDescription, {
									displayName: request.user.displayName,
								}),
					isAutoDismiss: true,
				});

				if (decision === 'APPROVE') {
					fireAnalyticEvent({
						eventType: 'sendTrackEvent',
						action: 'granted',
						actionSubject: 'access',
						actionSubjectId: 'grantAccess',
						source: REALTIME_ACCESS_REQUEST_FLAG,
						attributes: {
							accessType: request.requestAccessType,
							processingTime: Date.now() - request.creationDateTimestamp,
							creationDateTimestamp: request.creationDateTimestamp,
							...extractRestrictionInfo(restrictedUserPrincipalInfo),
						},
					});
				} else {
					fireAnalyticEvent({
						eventType: 'sendUIEvent',
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'denyAccess',
						source: REALTIME_ACCESS_REQUEST_FLAG,
						attributes: {
							accessType: request.requestAccessType,
							processingTime: Date.now() - request.creationDateTimestamp,
							creationDateTimestamp: request.creationDateTimestamp,
							...extractRestrictionInfo(restrictedUserPrincipalInfo),
						},
					});
				}
			} else if (requestAlreadyProcessed) {
				void flags.showInfoFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_INFO`,
					title: formatMessage(i18n.requestAlreadyProcessedTitle),
					isAutoDismiss: true,
				});

				fireAnalyticEvent({
					eventType: 'sendTrackEvent',
					action: 'alreadyProcessed',
					actionSubject: 'accessRequestsRealtimeFlagMutation',
					source: REALTIME_ACCESS_REQUEST_FLAG,
					attributes: {
						attemptedDecision: decision,
						accessType: request.requestAccessType,
						...extractRestrictionInfo(restrictedUserPrincipalInfo),
					},
				});
			} else {
				void flags.showErrorFlag({
					id: `${REALTIME_ACCESS_REQUEST_FLAG_ID}_ERROR`,
					title: formatMessage(i18n.errorResolvingRequestTitle),
					isAutoDismiss: true,
				});

				const err = result?.data?.confluence_updateContentAccessRequest?.errors?.[0];
				const message = err?.message || 'unknown';

				fireAnalyticEvent({
					eventType: 'sendOperationalEvent',
					action: 'failed',
					actionSubject: 'accessRequestsRealtimeFlagMutation',
					source: REALTIME_ACCESS_REQUEST_FLAG,
					attributes: {
						requestId: request.id,
						contentId,
						accessType: request.requestAccessType,
						accountId: request.creatorAaid,
						message,
					},
				});
			}
		},
		[
			cloudId,
			contentId,
			isRbacEnabled,
			flags,
			mutateRequest,
			resolveRestrictionsForSubjects,
			formatMessage,
			fireAnalyticEvent,
		],
	);

	const maybeShowRealtimeFlag = useCallback(
		async (data: useRealtimeAccessRequestsFlagsQuery$data, eventName?: string) => {
			// This is only used for analytics
			const queryTrigger = eventName ?? (isInitialLoad ? 'initialLoad' : 'tabVisible');

			if (isInitialLoad) {
				setIsInitialLoad(false);
			}

			const accessRequests = removeOwnRequests(
				data.confluence_getLatestPendingRequests?.latestPendingRequest ?? [],
				userId,
			);

			NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE.updateIndicatorVisibility(
				contentId,
				accessRequests[0]?.creationDateTimestamp ?? 0,
				eventName === 'contentAccessRequested' ? 'partial' : 'full',
				accessRequests?.length ?? 0,
			);

			if (accessRequests.length === 0) {
				// If a cached "last seen" timestamp for a realtime flag exists and it's > 5 minutes old
				// Then we can clear the cache to avoid buildup
				clearLastSeenRealtimeRequestCacheIfExpired(contentId);
				return;
			}

			// We just need to remove requests older than the "last seen" one in the cache
			// Also ensure all requests have bare minimum info to display flag correctly
			const filteredRequests = accessRequests.filter((request) => {
				const hasAllNecessaryData = request?.user?.displayName && request?.requestAccessType;
				const requestCreationTimestamp = Number(request?.creationDateTimestamp);
				const isMoreRecentThanLastSeenRequest = isMoreRecentThanCachedRequestTimestamp(
					contentId,
					requestCreationTimestamp,
				);

				return hasAllNecessaryData && isMoreRecentThanLastSeenRequest;
			});
			const latestIncomingRequestCreatedAt = Number(filteredRequests[0]?.creationDateTimestamp);

			if (filteredRequests.length === 0) {
				return;
			}

			// ID of any realtime flag that might be currently displayed
			const EXISTING_FLAG_ID = flags.state.flags.find(({ id }) =>
				id.toString().startsWith(REALTIME_ACCESS_REQUEST_FLAG),
			)?.id;

			// ID of the flag that we're about to display.
			// The suffix is a concatenated list of all the request ID-s displayed in the flag.
			// It helps us to know if the existing and new flag are going to be the same. See the edge case below
			const NEXT_FLAG_ID = `${REALTIME_ACCESS_REQUEST_FLAG_ID}_${makeFlagIdSuffix(filteredRequests)}`;

			/**
			 * EDGE CASE: Flag gets hidden and exact same flag gets shown in its place.
			 * This can happen because of tab inactivity monitoring. To reproduce:
			 *
			 * 1. Existing Realtime flag is showing on a page
			 * 2. You click away into another tab.
			 * 3. Click back on the original tab with the Realtime flag wihin 16s (autodismiss timeout)
			 *    - we refetch all requests because the tab is now active again
			 *    - the new request(s) are very likely the same as the ones already displayed in the flag
			 *    - The existing flag gets hidden and a new flag with the same request(s) gets shown
			 *    - To prevent this we just return early in this case
			 *
			 * This is likely to happen often as people click back and forth between tabs, so better to address it
			 */
			if (EXISTING_FLAG_ID === NEXT_FLAG_ID) {
				return;
			}

			/**
			 * We cache the request as "seen" when the flag for it gets destroyed
			 * However, if another realtime flag was shown WHILE the the current requests's flag is visible
			 * Then we don't want to cache the current request to allow the current request to continue
			 * showing in the newer flags' stacked display.
			 *
			 * As a reminder, requirements state that if a current flag is displayed and a new request comes in,
			 * then the current flag needs to be replaced by a new flag that shows both of the requests with a facepile
			 * for both users.
			 *
			 * If a third request comes in, then that second flag will get replaced with a third one, with the facepile
			 * now showing 3 users, etc.
			 *
			 * Thus, to allow for this stacking of requests to occur, we only want to cache the last seen request when
			 * the last flag in this request overlap sequence gets destroyed.
			 * */
			const onDestroy = () => {
				const skipCachingOfLastSeenFlag = flags.state.flags.find(
					({ id }) => `${id}`.startsWith(REALTIME_ACCESS_REQUEST_FLAG) && id !== NEXT_FLAG_ID,
				);

				if (!skipCachingOfLastSeenFlag) {
					updateLastSeenRealtimeRequestCache(contentId, latestIncomingRequestCreatedAt);
				}
			};

			let restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined;
			let resolveRestrictionsDialogProps: ResolveRestrictionsDialogProps | undefined;
			let onViewDetailsClick: () => void = NOOP;

			const hideFlag = () => {
				void flags.hideFlag(NEXT_FLAG_ID);
			};

			const onReviewRequestsClick = () => {
				onReviewRequests();
				hideFlag();

				fireAnalyticEvent({
					eventType: 'sendUIEvent',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'reviewRequests',
					source: REALTIME_ACCESS_REQUEST_FLAG,
					attributes: {
						trigger: queryTrigger,
						requestsDisplayed: filteredRequests.length,
					},
				});
			};

			if (filteredRequests.length === 1) {
				const request = filteredRequests[0];
				const user = request?.user;
				const accessType = request?.requestAccessType ?? 'VIEW';
				const addMode = accessType === 'EDIT' ? 'as-editor' : 'as-viewer';
				const userPrincipal = {
					id: user?.id ?? '',
					name: user?.displayName ?? '',
					avatarUrl: user?.photos?.find((photo) => photo?.isPrimary)?.value ?? '',
					email: '', // not needed
					knownErrors: undefined,
					sitePermissionType: accessType,
				};

				// Make the blocked access check
				const [
					{
						showResolveRestrictionsDialog,
						showWarnRestrictionsDialog,
						restrictedUserPrincipals,
						assignableSpaceRolesIntersection,
					},
				] = await Promise.all([
					validateRestrictedPrincipals({
						contentId,
						cloudId,
						edition,
						numberOfAncestors: 1, // not needed, API will error out if < 10 layers of ancestors
						addMode,
						stagedUserPrincipals: [userPrincipal],
						setMasterState: () => {}, // not needed
						fireAnalyticEvent,
					}),
					preloadSpaceRoleMode(cloudId),
				]);

				/** Early return if user is not empowered to resolve blocked access */
				if (showWarnRestrictionsDialog) {
					fireAnalyticEvent({
						eventType: 'sendUIEvent',
						action: 'aborted',
						actionSubject: 'realtimeFlag',
						source: REALTIME_ACCESS_REQUEST_FLAG,
						attributes: {
							trigger: queryTrigger,
							requestsDisplayed: filteredRequests.length,
							...extractRestrictionInfo(restrictedUserPrincipalInfo),
						},
					});

					return;
				}

				if (showResolveRestrictionsDialog && restrictedUserPrincipals?.[0]) {
					restrictedUserPrincipalInfo = restrictedUserPrincipals?.[0];

					onViewDetailsClick = () => {
						showModal(ResolveRestrictionsDialogWrapper, resolveRestrictionsDialogProps!);
						hideFlag();

						fireAnalyticEvent({
							eventType: 'sendUIEvent',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'viewDetails',
							source: REALTIME_ACCESS_REQUEST_FLAG,
							attributes: {
								trigger: queryTrigger,
								requestsDisplayed: filteredRequests.length,
								...extractRestrictionInfo(restrictedUserPrincipalInfo),
							},
						});
					};

					const approveRequest = () => {
						void onProcessRequestClick(request, 'APPROVE', restrictedUserPrincipalInfo);
						hideFlag();
					};

					resolveRestrictionsDialogProps = {
						contentId,
						restrictedUserPrincipals,
						assignableSpaceRolesIntersection,
						pageTitle,
						addMode,
						onComplete: approveRequest,
						onClose: NOOP,
						onCloseComplete: NOOP,
						onSkip: NOOP,
						showErrorFlag: flags.showErrorFlag,
						triggeredBy: 'realtimeAccessRequestFlag',
					};
				}
			}

			const onFlagCloseButtonClick = () => {
				fireAnalyticEvent({
					eventType: 'sendUIEvent',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'dismissRealtimeAccessRequestFlag',
					source: REALTIME_ACCESS_REQUEST_FLAG,
					attributes: {
						trigger: queryTrigger,
						requestsDisplayed: filteredRequests.length,
						...(filteredRequests.length === 1
							? extractRestrictionInfo(restrictedUserPrincipalInfo)
							: {}),
					},
				});
			};

			await flags.showCustomFlag({
				id: NEXT_FLAG_ID,
				isAutoDismiss: true,
				autoDismissSeconds: 16,
				customIcon: makeFlagCustomIcon(filteredRequests),
				onClose: onFlagCloseButtonClick,
				title: (
					<FlagRemovalDetector onDestroy={onDestroy}>
						{makeFlagTitle(filteredRequests, formatMessage)}
					</FlagRemovalDetector>
				),
				description: makeFlagDescription(
					filteredRequests,
					formatMessage,
					restrictedUserPrincipalInfo,
				),
				actions: makeFlagActions(
					filteredRequests,
					restrictedUserPrincipalInfo,
					onProcessRequestClick, // Approves / Denies request based on arguments
					onReviewRequestsClick, // Opens USD -> Access Requests
					onViewDetailsClick, // Opens standalone version of Resolve Restrictions Modal
					hideFlag,
					formatMessage,
				),
			});

			/**
			 * Fire flag shown analytic
			 */
			fireAnalyticEvent({
				eventType: 'sendUIEvent',
				action: 'shown',
				actionSubject: 'realtimeFlag',
				source: REALTIME_ACCESS_REQUEST_FLAG,
				attributes: {
					trigger: queryTrigger,
					requestsDisplayed: filteredRequests.length,
					...extractRestrictionInfo(restrictedUserPrincipalInfo),
				},
			});

			/**
			 * Only hide any existing flags AFTER the latest flag has been shown
			 * This ensures that the onDestroy callback of the existing flag
			 * Will realize there's a newer flag on the screen
			 * And will thus skip caching the EXISTING_FLAG_ID's request as seen
			 * This allows the request to coninue appearing in the facepile of any
			 * Newer flags that are displayed after it
			 */
			if (EXISTING_FLAG_ID) {
				await flags.hideFlag(EXISTING_FLAG_ID);
			}
		},
		[
			userId,
			cloudId,
			contentId,
			pageTitle,
			edition,
			onProcessRequestClick,
			onReviewRequests,
			fireAnalyticEvent,
			flags,
			showModal,
			formatMessage,
			isInitialLoad,
		],
	);

	const { refetch, error } = useQueryInterop<
		useRealtimeAccessRequestsFlagsQuery$data,
		useRealtimeAccessRequestsFlagsQuery$variables
	>(UseRealtimeAccessRequestsQuery, {
		variables: {
			cloudId,
			contentId,
			duration: ONE_YEAR_IN_MINUTES,
		},
		skip: skipConditions,
		fetchPolicy: 'network-only',
		// This "onCompleted" runs only on initial load.
		onCompleted: maybeShowRealtimeFlag,
		onError: markAsErrored,
	});

	if (error) {
		markErrorAsHandled(error);
		if (hasError === false) {
			setHasError(true);
		}
	}

	const onDataReceived = useCallback(
		async (eventName: string) => {
			try {
				/**
				 * Decide how far back in time we look to find existing pending requests.
				 *
				 * Option 1: eventName === 'contentAccessDecisionMade'
				 * If it's after an existing request was just approved / denied, we want ALL requests
				 * If no requests come back in response to that, then we can remove the 🔵 indicator
				 *
				 * Option 2: eventName === 'contentAccessRequested'
				 * If it's after a new access request was just created, we only want to fetch the last
				 * 5 minutes (or whatever short period we decide). This allows us to show a realtime flag
				 * only for newly created requests that haven't been seen yet.
				 * We cache the last seen realtime request creation date in localStorage.
				 */
				const lookbackPeriod =
					eventName === 'contentAccessDecisionMade' ? ONE_YEAR_IN_MINUTES : DEFAULT_MINUTE_LOOKBACK;

				const result = await refetch({ duration: lookbackPeriod, cloudId, contentId });

				await maybeShowRealtimeFlag(result.data, eventName);
			} catch (e) {
				markAsErrored(e);

				fireAnalyticEvent({
					eventType: 'sendOperationalEvent',
					action: 'failed',
					actionSubject: 'onDataReceived',
					actionSubjectId: eventName,
					source: REALTIME_ACCESS_REQUEST_FLAG,
					attributes: {
						name: e?.name,
						message: e?.message,
					},
				});
			}
		},
		[refetch, cloudId, contentId, markAsErrored, maybeShowRealtimeFlag, fireAnalyticEvent],
	);

	// Monitor when new pending requests get created
	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'contentAccessRequested',
		subscriberId: ConfluencePageAri.create({
			siteId: cloudId,
			pageId: contentId,
		}).toString(),
		onDataReceived,
		skip: skipConditions,
	});

	// Monitor the approval / denial of existing pending requests
	useSubscriptionEvent({
		contentId,
		contentType,
		delta: 'contentAccessDecisionMade',
		subscriberId: ConfluencePageAri.create({
			siteId: cloudId,
			pageId: contentId,
		}).toString(),
		onDataReceived,
		skip: skipConditions,
	});

	useEffect(() => {
		if (!isContentRestricted && !expVal('confluence_access_request_updates', 'isEnabled', false)) {
			NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE.hideIndicator(contentId);
		}
	}, [isContentRestricted, contentId]);

	// Cleanup effect when page is navigated away from
	useEffect(() => {
		return () => {
			// Subscribers need to be reset when the hook gets killed
			// This usually means that the share button got dismounted
			// And user navigated away from the current content
			NEW_ACCESS_REQUEST_INDICATOR_STATE_INSTANCE.resetSubscribers();

			// Databases and Whiteboards DON'T hide the flags when leaving the content
			// Live pages, regular pages DO hide the flags when leaving the content
			// Thus we choose to hide the flags everywhere to equalize behavior
			const existingFlagId = flags.state.flags.find(({ id }) =>
				id.toString().startsWith(REALTIME_ACCESS_REQUEST_FLAG),
			)?.id;

			if (existingFlagId) {
				void flags.hideFlag(existingFlagId);
			}
		};
	}, [flags]);
};
